import PropTypes from 'prop-types';
import { useEffect } from 'react';
import * as Yup from 'yup';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// @mui
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider, RHFSelect, RHFTextField } from '../../../components/hook-form';

import { getAuditBranches } from '../../branches/store/branchActions';
import { filterUsers, getUsers } from '../store/userActions';

const ROLES = [
  {
    id: 1,
    label: 'Admin',
    value: 'ADMIN',
  },

  {
    id: 2,
    label: 'Auditor',
    value: 'AUDITOR',
  },
  { id: 8, label: 'Director', value: 'DIRECTOR' },
  {
    id: 5,
    label: 'Data Entry',
    value: 'DATA_ENTRY',
  },
  {
    id: 2,
    label: 'Manager',
    value: 'MANAGER',
  },

  {
    id: 6,
    label: 'Service Provider',
    value: 'SERVICE_PROVIDER',
  },
];

UsersFilter.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};

export default function UsersFilter({ open, handleClose }) {
  const dispatch = useDispatch();
  const { auditBranches } = useSelector((state) => state.branchReducer);

  const filterSchema = Yup.object().shape({});

  const defaultValues = {
    locationId: '',
    role: '',
    name: '',
  };

  const methods = useForm({
    resolver: yupResolver(filterSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    // control,
    // setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  // Check if any filters are applied
  const hasFilters = values.locationId || values.role || values.name?.trim();

  const onSubmit = async (data) => {
    const payload = {
      locationId: data.locationId || '',
      role: data.role || '',
      name: data.name?.trim() || '',
    };
    dispatch(filterUsers(payload));
    handleClose();
  };

  const handleClearFilters = () => {
    // Reset form to default values
    reset(defaultValues);

    // Fetch all users without filters
    dispatch(getUsers({ page: 1, rowsPerPage: 10 }));

    // Close the dialog
    handleClose();
  };
  useEffect(() => {
    dispatch(getAuditBranches({}));
  }, [dispatch]);
  return (
    <Dialog
      maxWidth="md"
      open={open}
      fullWidth
      keepMounted
      onClose={handleClose}
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon color="primary" />
          <Typography variant="h6" component="span">
            Filter Users
          </Typography>
          {hasFilters && (
            <Typography variant="body2" color="primary" sx={{ ml: 1 }}>
              ({Object.values(values).filter((v) => v && v.toString().trim()).length} active)
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
              Use the fields below to filter users. Leave fields empty to include all options.
            </Typography>

            <RHFSelect
              fullWidth
              name="locationId"
              label="Branch"
              placeholder="All Branches"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
            >
              <MenuItem value="">
                <em>All Branches</em>
              </MenuItem>
              {auditBranches?.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </RHFSelect>

            <RHFSelect
              fullWidth
              name="role"
              label="Role"
              placeholder="All Roles"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
            >
              <MenuItem value="">
                <em>All Roles</em>
              </MenuItem>
              {ROLES?.map((option) => (
                <MenuItem key={option.id} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>

            <RHFTextField
              name="name"
              label="Name"
              placeholder="Search by name..."
              helperText="Enter full name or partial name to search"
            />

            {hasFilters && (
              <Box sx={{ p: 2, backgroundColor: 'primary.lighter', borderRadius: 1 }}>
                <Typography variant="body2" color="primary.main" fontWeight="medium">
                  Active Filters:
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  {values.locationId && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1,
                        py: 0.5,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: 1,
                      }}
                    >
                      Branch: {auditBranches?.find((b) => b.id === values.locationId)?.name}
                    </Typography>
                  )}
                  {values.role && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1,
                        py: 0.5,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: 1,
                      }}
                    >
                      Role: {ROLES?.find((r) => r.value === values.role)?.label}
                    </Typography>
                  )}
                  {values.name?.trim() && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1,
                        py: 0.5,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: 1,
                      }}
                    >
                      Name: {values.name}
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button size="small" variant="outlined" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>

          {hasFilters && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              disabled={isSubmitting}
            >
              Clear All
            </Button>
          )}

          <LoadingButton
            type="submit"
            size="small"
            variant="contained"
            loading={isSubmitting}
            startIcon={<FilterListIcon />}
            loadingPosition="start"
          >
            Apply Filters
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
